import {useCallback, useEffect, useState} from 'react';

import {tierService} from '@/services/tierService';
import type {AdminAfriexPerson} from '@/types';
import type {AfriexQueryParams} from '@/types/AfriexQueryParams';

const useTierUserList = (tierId: string) => {
  const [tierUserList, setTierUserList] = useState<
    Partial<AdminAfriexPerson>[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [itemTotal, setItemTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [error, setError] = useState<unknown>(null);

  const fetchTierUserList = useCallback(
    async (params: AfriexQueryParams) => {
      try {
        setError(null);
        setIsLoading(true);
        const {data, total} = await tierService.getTierUserList(tierId, params);
        setTierUserList(data);
        setItemTotal(total || 0);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [tierId],
  );

  useEffect(() => {
    // `fetchTierUserList` try/catches into `setError`, so this only fires if
    // that catch block itself throws. Routed to the same state the fetcher
    // uses, so the screen's existing error surface still reflects it.
    fetchTierUserList({}).catch((e: unknown) => setError(e));
  }, [fetchTierUserList]);

  return {
    error,
    fetchList: fetchTierUserList,
    isLoading,
    page,
    setPage,
    total: itemTotal,
    list: tierUserList,
  };
};

export default useTierUserList;
